import React from "react";
import SEO from "./SEO";
import { LegalArticle } from "../../lib/getLegalArticles";

// Helper function to safely access values, returning null if value is null, undefined, or an empty string
const safeValue = (value: any): any => {
  if (value === null || value === undefined || value === "") return null;
  return value;
};

interface LegalesSEOProps {
  activeTab: string;
  selectedArticle: LegalArticle | null;
}

const tabTitles: Record<string, string> = {
  terms: "Términos y Condiciones",
  privacy: "Política de Privacidad",
  shipping: "Política de Envíos",
  returns: "Cambios y Devoluciones",
  promotions: "Términos de Promociones",
};

const tabDescriptions: Record<string, string> = {
  terms: "Lee los términos y condiciones de Evefant.",
  privacy: "Conoce nuestra política de privacidad y cómo protegemos tus datos.",
  shipping: "Consulta nuestra política de envíos y entregas.",
  returns: "Infórmate sobre nuestra política de cambios y devoluciones.",
  promotions:
    "Descubre los términos y condiciones de nuestras promociones vigentes.",
};

const LegalesSEO: React.FC<LegalesSEOProps> = ({
  activeTab,
  selectedArticle,
}) => {
  // Prioritize backend SEO data if available
  const backendSEO = selectedArticle?.seo;

  // Fallback values for manual generation
  const fallbackTitle =
    safeValue(selectedArticle?.title) ||
    tabTitles[activeTab] ||
    "Información Legal - Evefant";
  const fallbackDescription =
    safeValue(selectedArticle?.description) ||
    tabDescriptions[activeTab] ||
    "Información legal y políticas de Evefant.";
  const fallbackImage =
    safeValue(selectedArticle?.image?.url) || "/og-legales.jpg";

  // Use backend SEO data or fallback to manual generation, using safeValue
  const title = safeValue(backendSEO?.title) || fallbackTitle;
  const description = safeValue(backendSEO?.description) || fallbackDescription;
  const image = safeValue(backendSEO?.image) || fallbackImage;
  const url =
    typeof window !== "undefined"
      ? window.location.href
      : "https://evefant.com/legales";

  // Use backend structured data if available, otherwise generate manually
  const schema =
    safeValue(backendSEO?.structuredData) ||
    (selectedArticle
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: safeValue(selectedArticle.title) || fallbackTitle,
          description:
            safeValue(selectedArticle.description) || fallbackDescription,
          image: image,
          url: url,
          datePublished: safeValue(selectedArticle.date),
          author: {
            "@type": "Organization",
            name: "Mega Osaka",
          },
        }
      : {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          description: description,
          url: url,
        });

  // Use backend OpenGraph if available, otherwise generate manually
  const openGraph = safeValue(backendSEO?.openGraph) || {
    "og:title": title,
    "og:description": description,
    "og:image": { url: image },
    "og:url": url,
    "og:type": selectedArticle ? "article" : "website",
  };

  return (
    <SEO
      title={title}
      description={description}
      image={image}
      type={selectedArticle ? "article" : "website"}
      schema={schema}
      openGraph={openGraph}
      seoData={backendSEO} // Pass backend SEO data to base component
      useGlobalFallbacks={true} // Ensure global fallbacks are used
    >
      <meta
        name="keywords"
        content={
          safeValue(backendSEO?.keywords) ||
          "términos, condiciones, privacidad, envíos, devoluciones, promociones, Evefant"
        }
      />
      <meta name="language" content="es" />
      <meta name="copyright" content="Evefant" />
      <meta name="author" content="Evefant" />
      <meta property="og:locale" content="es_PE" />
      <meta property="og:site_name" content="Evefant" />
      {backendSEO?.noIndex && <meta name="robots" content="noindex,nofollow" />}
      {backendSEO?.canonicalUrl && (
        <link rel="canonical" href={backendSEO.canonicalUrl} />
      )}
    </SEO>
  );
};

export default LegalesSEO;
